import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Stack,
  Alert,
  Skeleton
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import HomeBlogCard from '../components/cardComponents/HomeBlogCard';
import { useBlogList } from '../hooks/useBlogList';

const quickLinks = [
  { page: "How to Host", url: "/host", icon: "🏠" },
  { page: "Community", url: "/host", icon: "👥" },
  { page: "Hosting Ideas", url: "/host", icon: "💡" },
  { page: "Partner With Us", url: "/about", icon: "🤝" },
];

const Home = () => {
  const navigate = useNavigate();
  // Use the same hook as BlogList.js to fetch from markdown files
  const { blogs: allBlogs, isLoading, error } = useBlogList();

  const handleBlogClick = (post) => {
    // Use slug if available, otherwise use _id
    const slug = post.slug || post._id;
    navigate(`/blogentry/${slug}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 6 } }}>
        {/* Hero Section */}
        <Box
          component="section"
          sx={{
            mb: { xs: 4, md: 6 },
            textAlign: { xs: 'center', md: 'left' }
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#F5B935',
              fontWeight: 700,
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: { xs: '0.875rem', md: '1rem' },
              fontFamily: 'Arima, sans-serif'
            }}
          >
            a Lighthouse Ministry
          </Typography>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              color: '#487AFA',
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
              lineHeight: 1.2
            }}
          >
            Vacationing with a Ministry Mindset
          </Typography>
          <Box
            sx={{
              width: { xs: '60px', md: '100px' },
              height: '4px',
              bgcolor: '#F5B935',
              mx: { xs: 'auto', md: 0 },
              borderRadius: 1
            }}
          />
        </Box>

        {/* Featured Article */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <HomeBlogCard />
        </Box>

        {/* Main Content Area */}
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Quick Links Sidebar */}
          <Grid item xs={12} md={4} lg={3}>
            <Box
              component="aside"
              sx={{
                position: { md: 'sticky' },
                top: { md: 24 },
                mb: { xs: 4, md: 0 }
              }}
            >
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{ mb: 3, fontWeight: 600 }}
              >
                Quick Links
              </Typography>
              <Stack spacing={2}>
                {quickLinks.map((link) => (
                  <Card
                    key={link.url}
                    component={Link}
                    to={link.url}
                    sx={{
                      p: 2.5,
                      textDecoration: 'none',
                      bgcolor: '#487AFA',
                      color: 'white',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#3a6ae8',
                        transform: 'translateX(4px)',
                        boxShadow: 3
                      },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: '#487AFA',
                        outlineOffset: 2
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {link.icon} {link.page}
                    </Typography>
                  </Card>
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Blog Posts Grid */}
          <Grid item xs={12} md={8} lg={9}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mb: 3, fontWeight: 600 }}
            >
              Recent Posts
            </Typography>

            {/* Error State */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {typeof error === 'string' ? error : 'Failed to load blog posts. Please try again later.'}
              </Alert>
            )}

            {/* Loading State */}
            {isLoading ? (
              <Grid container spacing={2}>
                {[1, 2, 3].map((i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Card>
                      <Skeleton variant="rectangular" height={160} />
                      <CardContent>
                        <Skeleton variant="text" width="80%" height={32} />
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="40%" />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              /* Blog Posts */
              allBlogs.length === 0 ? (
                <Alert severity="info">
                  No blog posts available at this time.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {allBlogs.map((post) => (
                    <Grid item xs={12} sm={6} md={4} key={post._id || post.slug}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s ease-in-out',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: 6
                          },
                          '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: '#487AFA',
                            outlineOffset: 2
                          }
                        }}
                        onClick={() => handleBlogClick(post)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleBlogClick(post);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Read blog post: ${post.title}`}
                      >
                        {post.featuredImage && (
                          <CardMedia
                            component="img"
                            height="160"
                            image={post.featuredImage}
                            alt={post.title}
                            sx={{ objectFit: 'cover' }}
                          />
                        )}
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontWeight: 600
                            }}
                          >
                            {post.title}
                          </Typography>
                          {post.excerpt && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mb: 2
                              }}
                            >
                              {post.excerpt}
                            </Typography>
                          )}
                          {post.categories && post.categories.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                              {post.categories.slice(0, 2).map((cat) => (
                                <Chip
                                  key={cat}
                                  label={cat}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(post.publishedDate || post.createdAt)}
                          </Typography>
                          <Button size="small" color="primary" variant="text">
                            Read More →
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;